// Tyres: each tyre followed from fitting to scrapping - where it is, how many km it has run, what it cost per km.
import { useState } from "react";
import { Page, Card, Btn, Tabs, Table, Tr, Td, Field, TextIn, Loading, Pill, money } from "@/components/kit";
import api from "@/lib/api";
import { act, useLoad, useCan, useRegisters, Select, DateIn, Num, n, opts } from "@/components/erp";
import { fmtDate, today } from "@/lib/money";

const POS = ["FL", "FR", "R1LO", "R1LI", "R1RI", "R1RO", "R2LO", "R2LI", "R2RI", "R2RO", "R3LO", "R3LI", "R3RI", "R3RO", "SPARE"].map((v) => ({ value: v, label: v }));
const REASONS = [["worn", "Worn out"], ["puncture", "Puncture"], ["burst", "Burst"], ["damage", "Damaged"], ["rotation", "Rotation"], ["other", "Other"]].map(([value, label]) => ({ value, label }));
const AFTER = [["stock", "Back to stock (still usable)"], ["scrap", "Scrap"], ["sold", "Sold"]].map(([value, label]) => ({ value, label }));
const TONE = { fitted: "green", in_stock: "blue", scrapped: "grey", sold: "grey" };
const km = (v) => (v == null ? "—" : Math.round(v).toLocaleString("en-KE"));

export default function Tyres() {
  const can = useCan();
  const [tab, setTab] = useState("truck");
  const tabs = [{ value: "truck", label: "By truck" }, { value: "all", label: "All tyres" }, { value: "due", label: "Nearing end of life" }, { value: "brands", label: "Cost per km" }];
  return (
    <Page title="Tyres">
      <Tabs value={tab} onChange={setTab} tabs={tabs} />
      {tab === "truck" && <ByTruck can={can} />}
      {tab === "all" && <All can={can} />}
      {tab === "due" && <Due />}
      {tab === "brands" && <Brands />}
    </Page>
  );
}

function Actions({ t, can, done }) {
  const [m, setM] = useState(null);
  const regs = useRegisters(["trucks"]);
  if (!can("tyres.manage")) return null;
  const go = async () => {
    const body = { odometer: n(m.odometer) || 0, date: m.date || today(), reason: m.reason || "other", note: m.note || "", after: m.after || "stock", position: m.position, truck_id: m.truck_id || undefined, cost: n(m.cost) || 0 };
    const path = m.kind;
    if (await act(() => api.post(`/tyres/${t.id}/${path}`, body), "Done")) { setM(null); done(); }
  };
  return (
    <>
      <div className="flex flex-wrap gap-1">
        {t.status === "fitted" && <><Btn onClick={() => setM({ kind: "rotate", truck_id: t.truck_id })}>Move</Btn><Btn onClick={() => setM({ kind: "remove", after: "stock", reason: "worn" })}>Take off</Btn></>}
      </div>
      {m && (
        <Card title={`${t.serial} · ${m.kind === "rotate" ? "move to another position" : "take off"}`} className="mt-2 bg-white">
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Truck odometer now (km)"><Num value={m.odometer} onChange={(v) => setM({ ...m, odometer: v })} /></Field>
            {m.kind === "rotate" && <><Field label="Truck"><Select empty={null} value={m.truck_id} options={opts(regs.trucks, "plate")} onChange={(v) => setM({ ...m, truck_id: v })} /></Field>
              <Field label="Position"><Select value={m.position} options={POS} onChange={(v) => setM({ ...m, position: v })} /></Field></>}
            {m.kind === "remove" && <><Field label="Why"><Select empty={null} value={m.reason} options={REASONS} onChange={(v) => setM({ ...m, reason: v })} /></Field>
              <Field label="Then"><Select empty={null} value={m.after} options={AFTER} onChange={(v) => setM({ ...m, after: v })} /></Field>
              <div className="sm:col-span-3"><Field label="Note (needed if scrapped early)"><TextIn value={m.note} onChange={(v) => setM({ ...m, note: v })} /></Field></div></>}
            <Field label="Date"><DateIn value={m.date || today()} onChange={(v) => setM({ ...m, date: v })} /></Field>
          </div>
          <div className="mt-3 flex gap-2"><Btn kind="primary" onClick={go}>Save</Btn><Btn onClick={() => setM(null)}>Cancel</Btn></div>
        </Card>)}
    </>
  );
}

function TyreRows({ rows, can, reload, showTruck }) {
  return (
    <Table minWidth={900} cols={[{ label: "Serial" }, { label: "Tyre" }, ...(showTruck ? [{ label: "Where" }] : [{ label: "Position" }]), { label: "Km run", align: "right" }, { label: "Life used", align: "right" }, { label: "Cost/km", align: "right" }, { label: "Status" }, { label: "" }]}>
      {rows.map((t) => (
        <Tr key={t.id}><Td className="font-medium">{t.serial}</Td><Td>{t.brand} {t.size}<div className="text-xs text-slate-500">cost {money(t.cost)}</div></Td>
          <Td>{showTruck ? (t.truck ? `${t.truck} ${t.position}` : "—") : t.position}</Td><Td right>{km(t.km_total)}</Td><Td right>{t.life_used_pct != null ? `${t.life_used_pct}%` : "—"}</Td>
          <Td right>{t.cost_per_km != null ? t.cost_per_km.toFixed(2) : "—"}</Td><Td><Pill kind={TONE[t.status] || "grey"}>{t.status.replace("_", " ")}</Pill></Td>
          <Td><Actions t={t} can={can} done={reload} /></Td></Tr>))}
    </Table>
  );
}

function ByTruck({ can }) {
  const regs = useRegisters(["trucks"]);
  const [truck, setTruck] = useState(null);
  const { data, reload } = useLoad(truck ? "/tyres" : null, { truck_id: truck, status: "fitted" });
  const [f, setF] = useState(null);
  const fit = async () => {
    if (await act(() => api.post("/tyres/fit", { truck_id: truck, position: f.position, serial: f.serial, brand: f.brand || "", size: f.size || "", condition: f.condition || "new", cost: n(f.cost) || 0, odometer: n(f.odometer) || 0, date: f.date || today(), job_ref: f.job_ref || "" }), "Tyre fitted")) { setF(null); reload(); }
  };
  return (
    <>
      <div className="mt-3 max-w-xs"><Field label="Truck"><Select value={truck} options={opts(regs.trucks, "plate")} onChange={setTruck} /></Field></div>
      {truck && can("tyres.manage") && !f && <div className="mt-2"><Btn kind="primary" onClick={() => setF({ condition: "new", date: today() })}>+ Fit a tyre</Btn></div>}
      {f && (
        <Card title="Fit a tyre" className="mt-3">
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Position"><Select value={f.position} options={POS} onChange={(v) => setF({ ...f, position: v })} /></Field>
            <Field label="Serial number (on the sidewall)"><TextIn value={f.serial} onChange={(v) => setF({ ...f, serial: v })} /></Field>
            <Field label="Brand"><TextIn value={f.brand} onChange={(v) => setF({ ...f, brand: v })} /></Field>
            <Field label="Size"><TextIn value={f.size} onChange={(v) => setF({ ...f, size: v })} /></Field>
            <Field label="Cost (KES) - only for a new serial number"><Num value={f.cost} onChange={(v) => setF({ ...f, cost: v })} /></Field>
            <Field label="Truck odometer now (km)"><Num value={f.odometer} onChange={(v) => setF({ ...f, odometer: v })} /></Field>
            <Field label="Job card"><TextIn value={f.job_ref} onChange={(v) => setF({ ...f, job_ref: v })} /></Field>
            <Field label="Date"><DateIn value={f.date} onChange={(v) => setF({ ...f, date: v })} /></Field>
          </div>
          <div className="mt-3 flex gap-2"><Btn kind="primary" onClick={fit}>Fit it</Btn><Btn onClick={() => setF(null)}>Cancel</Btn></div>
        </Card>)}
      {truck && (!data ? <Loading /> : <><TyreRows rows={data} can={can} reload={reload} />{!data.length && <p className="mt-4 text-sm text-slate-500">No tyres recorded on this truck yet.</p>}</>)}
    </>
  );
}

function All({ can }) {
  const [status, setStatus] = useState("");
  const { data, reload } = useLoad("/tyres", { status: status || undefined });
  return (
    <>
      <div className="mt-3 max-w-xs"><Select empty="Every tyre" value={status} onChange={(v) => setStatus(v || "")}
        options={[["fitted", "Fitted"], ["in_stock", "In stock"], ["scrapped", "Scrapped"], ["sold", "Sold"]].map(([value, label]) => ({ value, label }))} /></div>
      {!data ? <Loading /> : <TyreRows rows={data} can={can} reload={reload} showTruck />}
    </>
  );
}

function Due() {
  const { data } = useLoad("/tyres/due");
  if (!data) return <Loading />;
  return (
    <>
      <p className="mt-3 text-sm text-slate-600">Fitted tyres at 90% or more of their expected life (100,000 km), by the truck's last odometer reading.</p>
      <Table minWidth={640} cols={[{ label: "Tyre" }, { label: "Where" }, { label: "Km now", align: "right" }, { label: "Life", align: "right" }]}>
        {data.map((t) => (<Tr key={t.id}><Td className="font-medium">{t.serial}<div className="text-xs text-slate-500">{t.brand} {t.size}</div></Td><Td>{t.truck} {t.position}</Td><Td right>{km(t.km_now)}</Td>
          <Td right>{t.over ? <Pill kind="red">over</Pill> : <Pill kind="amber">nearly</Pill>} {km(t.life_km)}</Td></Tr>))}
      </Table>
      {!data.length && <p className="mt-4 text-sm text-slate-500">None yet.</p>}
    </>
  );
}

function Brands() {
  const { data } = useLoad("/tyres/report");
  if (!data) return <Loading />;
  return (
    <>
      <p className="mt-3 text-sm text-slate-600">What each brand has cost per kilometre run - the cheapest per km is the best buy, not the cheapest price.</p>
      <Table minWidth={620} cols={[{ label: "Brand" }, { label: "Tyres", align: "right" }, { label: "Km run", align: "right" }, { label: "Cost", align: "right" }, { label: "Cost / km", align: "right" }, { label: "Early scrap", align: "right" }]}>
        {data.map((b) => (<Tr key={b.brand}><Td className="font-medium">{b.brand}</Td><Td right>{b.tyres}</Td><Td right>{km(b.km)}</Td><Td right>{money(b.cost)}</Td>
          <Td right>{b.cost_per_km != null ? b.cost_per_km.toFixed(2) : "—"}</Td><Td right>{b.scrapped_early}</Td></Tr>))}
      </Table>
      {!data.length && <p className="mt-4 text-sm text-slate-500">No tyres recorded yet.</p>}
    </>
  );
}
